import React, { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Upload,
  X,
  FileText,
  FileSpreadsheet,
  Presentation,
  File
} from 'lucide-react';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/LoadingSpinner';

// Accepted document MIME types
const ACCEPTED_TYPES = {
  'application/pdf': { icon: FileText, label: 'PDF', color: 'text-red-500' },
  'application/msword': { icon: FileText, label: 'Word', color: 'text-blue-500' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: FileText, label: 'Word', color: 'text-blue-500' },
  'application/vnd.ms-excel': { icon: FileSpreadsheet, label: 'Excel', color: 'text-green-500' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: FileSpreadsheet, label: 'Excel', color: 'text-green-500' },
  'application/vnd.ms-powerpoint': { icon: Presentation, label: 'PowerPoint', color: 'text-orange-500' },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { icon: Presentation, label: 'PowerPoint', color: 'text-orange-500' },
  'text/plain': { icon: FileText, label: 'Text', color: 'text-gray-500' },
  'text/csv': { icon: FileSpreadsheet, label: 'CSV', color: 'text-green-500' },
  'application/rtf': { icon: FileText, label: 'RTF', color: 'text-gray-500' }
};

// File extensions for accept attribute
const ACCEPTED_EXTENSIONS = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf';

// Format file size for display
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Get icon component for a file type
const getFileIcon = (mimeType) => {
  const typeInfo = ACCEPTED_TYPES[mimeType];
  return typeInfo?.icon || File;
};

// Get icon color for a file type
const getFileIconColor = (mimeType) => {
  const typeInfo = ACCEPTED_TYPES[mimeType];
  return typeInfo?.color || 'text-gray-500';
};

// Get label for a file type
const getFileTypeLabel = (mimeType) => {
  const typeInfo = ACCEPTED_TYPES[mimeType];
  return typeInfo?.label || 'Document';
};

export function DocumentUpload({
  companyId,
  onUploadComplete,
  onRemove,
  uploadedDocument = null,
  disabled = false
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingMessage, setUploadingMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const handleUpload = useCallback(async (file) => {
    if (!file) return;

    // Validate file type
    if (!ACCEPTED_TYPES[file.type]) {
      toast.error('Unsupported file type. Please upload PDF, Word, Excel, PowerPoint, TXT, CSV, or RTF files.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadingMessage('Preparing upload...');

    try {
      // Generate temporary post ID for folder structure
      const tempPostId = `post-${Date.now()}-${Math.random().toString(36).substring(2)}`;
      const folderPath = `${companyId}/${tempPostId}`;
      const fileExt = file.name.split('.').pop();
      const safeFileName = `document.${fileExt}`;

      // Get signed upload URL from Edge Function
      setUploadingMessage('Getting upload URL...');
      const { data: urlData, error: urlError } = await supabase.functions.invoke(
        'generate-upload-url',
        {
          body: {
            fileName: safeFileName,
            contentType: file.type,
            folderPath: folderPath,
          },
        }
      );

      if (urlError || !urlData?.signedUrl || !urlData?.publicUrl) {
        throw new Error('Failed to get upload URL');
      }

      // Upload document to GCS using XMLHttpRequest for progress tracking
      setUploadingMessage('Uploading document...');

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(percentComplete);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject(new Error('Upload failed'));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

        xhr.open('PUT', urlData.signedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      // Document uploaded successfully
      const documentData = {
        url: urlData.publicUrl,
        name: file.name,
        type: file.type,
        size: file.size,
        text: null
      };

      // Call onUploadComplete with basic data
      // Text extraction is disabled until N8N webhook is configured
      onUploadComplete?.(documentData);
      toast.success('Document uploaded successfully');
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadingMessage('');
    }
  }, [companyId, onUploadComplete]);

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
    // Reset input value to allow re-uploading same file
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !uploading) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled || uploading) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleRemove = () => {
    onRemove?.();
  };

  // If document is already uploaded, show the preview
  if (uploadedDocument) {
    const IconComponent = getFileIcon(uploadedDocument.type);
    const iconColor = getFileIconColor(uploadedDocument.type);
    const typeLabel = getFileTypeLabel(uploadedDocument.type);

    return (
      <div className='space-y-3'>
        <div className='flex items-center justify-between p-4 border rounded-lg bg-muted/30'>
          <div className='flex items-center gap-3 min-w-0'>
            <div className={`flex-shrink-0 ${iconColor}`}>
              <IconComponent className='h-8 w-8' />
            </div>
            <div className='min-w-0'>
              <p className='font-medium truncate'>{uploadedDocument.name}</p>
              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                <span>{typeLabel}</span>
                <span>•</span>
                <span>{formatFileSize(uploadedDocument.size)}</span>
              </div>
            </div>
          </div>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            onClick={handleRemove}
            disabled={disabled}
            className='flex-shrink-0'
          >
            <X className='h-4 w-4' />
          </Button>
        </div>
      </div>
    );
  }

  // Upload dropzone
  return (
    <div
      className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
        isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
      } ${disabled || uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        type='file'
        accept={ACCEPTED_EXTENSIONS}
        onChange={handleFileInput}
        className='hidden'
        id='document-upload'
        disabled={disabled || uploading}
      />
      <label
        htmlFor='document-upload'
        className={`flex flex-col items-center gap-2 w-full ${
          disabled || uploading ? 'cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        {uploading ? (
          <div className='flex flex-col items-center gap-2 w-full px-4'>
            <LoadingSpinner />
            <p className='text-sm font-medium'>{uploadingMessage}</p>
            {uploadProgress > 0 && (
              <div className='w-full max-w-xs'>
                <div className='flex justify-between text-xs text-muted-foreground mb-1'>
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className='w-full bg-muted rounded-full h-2'>
                  <div
                    className='bg-primary h-2 rounded-full transition-all duration-300'
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <Upload className='h-12 w-12 text-muted-foreground' />
            <div>
              <p className='font-medium'>
                {isDragging ? 'Drop document here' : 'Drag & drop document here'}
              </p>
              <p className='text-sm text-muted-foreground'>or click to browse</p>
            </div>
            <div className='flex flex-wrap justify-center gap-1 mt-2'>
              <Badge variant='outline' className='text-xs'>PDF</Badge>
              <Badge variant='outline' className='text-xs'>Word</Badge>
              <Badge variant='outline' className='text-xs'>Excel</Badge>
              <Badge variant='outline' className='text-xs'>PowerPoint</Badge>
              <Badge variant='outline' className='text-xs'>Text</Badge>
            </div>
          </>
        )}
      </label>
    </div>
  );
}

// Export utility functions for use elsewhere
export { formatFileSize, getFileIcon, getFileIconColor, getFileTypeLabel, ACCEPTED_TYPES };
